import { html, nothing, type TemplateResult } from 'lit';

export type StatusTone = 'positive' | 'caution' | 'critical' | 'info' | 'neutral';

/** How a campaign status is shown: a tone for its dot, and its word. */
export interface StatusLook {
  tone: StatusTone;
  label: string;
  /** Finished or put away: the dot is a ring and the word is muted. */
  hollow?: boolean;
  /** Sending now: the dot pulses (and stops under reduced motion). */
  live?: boolean;
}

const LOOKS: Record<string, StatusLook> = {
  active: { tone: 'positive', label: 'Active', live: true },
  paused: { tone: 'caution', label: 'Paused' },
  draft: { tone: 'neutral', label: 'Draft' },
  generating: { tone: 'info', label: 'Generating' },
  ready_to_launch: { tone: 'caution', label: 'Ready to launch' },
  preparing_to_launch: { tone: 'caution', label: 'Cool-down' },
  completed: { tone: 'neutral', label: 'Completed', hollow: true },
  archived: { tone: 'neutral', label: 'Archived', hollow: true },
  error: { tone: 'critical', label: 'Error' },
};

/** `waiting_for_review` → `Waiting for review`: a status the client does not know still says what it is. */
export function sentence(status: string): string {
  const words = String(status ?? '')
    .replace(/[_-]+/g, ' ')
    .trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : 'Unknown';
}

/** The one place a campaign status gets its tone and word. */
export function campaignStatus(status: string): StatusLook {
  return LOOKS[status] ?? { tone: 'neutral', label: sentence(status) };
}

/** Any status as the design system shows it: a dot in its tone and a word, no chip. Lead states use it too. */
export function toneDot(look: StatusLook, title?: string): TemplateResult {
  return html`<span
    class="ndi-status ndi-status-${look.tone} ${look.hollow ? 'ndi-status-hollow' : ''}"
    title=${title ?? nothing}
    >${look.live
      ? html`<span class="ndi-live" aria-hidden="true"></span>`
      : html`<span class="ndi-status-dot" aria-hidden="true"></span>`}${look.label}</span
  >`;
}

/** A campaign status as the design system shows it: a coloured dot and a word, no chip. */
export function statusDot(status: string): TemplateResult {
  return toneDot(campaignStatus(status));
}
